import { useEffect, useState } from "react";
import { datadogRum } from "@datadog/browser-rum";
import { UserProfile } from "../typings";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../conf/firebaseConfig";
import { genericConverter } from "../services/firestoreService";

interface UseUserRealTimeOptions {
  enabled?: boolean;
}

export const useUserRealTime = (
  userId: string,
  options?: UseUserRealTimeOptions
) => {
  const enabled = options?.enabled ?? true;
  const [user, setUser] = useState<Partial<UserProfile> | null>(null);

  useEffect(() => {
    if (!userId || !enabled) {
      return;
    }

    // Reference to the Firestore document
    const docRef = doc(db, "users", userId).withConverter(
      genericConverter<Partial<UserProfile>>()
    );

    // Set up the real-time listener
    const unsubscribe = onSnapshot(
      docRef,
      (document) => {
        if (document.exists()) {
          setUser({ id: document.id, ...document.data() });
        } else {
          console.log("No such document!");
        }
      },
      (error) => {
        console.error("Error fetching real-time updates: ", error);

        // Track error in Datadog RUM for monitoring
        datadogRum.addError(error, {
          hook: "useUserRealTime",
          collection: "users",
          userId,
          errorCode: error.code,
          errorType: "firestore_listener_error",
        });
      }
    );

    // Clean up the subscription when the component unmounts
    return () => unsubscribe();
  }, [userId, enabled]);

  return user;
};
